import React, { createContext, useContext, useState } from "react";
import extractFileSpecificCode from "../utils/string/extractFileSpecificCode";
import { useLocalFileActionsContext } from "../contexts/LocalFileActionsContext";
import { useRemoteFileActionsContext } from "../contexts/RemoteFileActionsContext";
import { useImageSelector } from "../contexts/ImageSelectorContext";
import { useGlobalNetworkState } from "../contexts/GlobalNetworkStateContext";
import { FirebaseContext } from "./FirebaseContext";
import { useIDEContext } from "../contexts/IDEContext";
import { SessionContext } from "../contexts/SessionContext";

const OpenAIChatContext = createContext();

export const OpenAIChatProvider = ({ children }) => {
  const { tabs, updateTabContent, addTab } = useLocalFileActionsContext();
  const { images, removeImage } = useImageSelector();
  const { showLoading, hideLoading } = useGlobalNetworkState();
  const { quickMode, selectedRepoName } = useIDEContext();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const { getFreshToken } = useContext(FirebaseContext);
  const { getEndpointUrl } = useContext(SessionContext);
  const { updateAddFiles, buildProject, serveProject } =
    useRemoteFileActionsContext();

  const convertImageToBase64 = async (imagePath) => {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(`data:image/jpeg;base64,${reader.result.split(",")[1]}`);
      reader.readAsDataURL(blob);
    });
  };

  const sendRequestToOpenAI = async (input, model, processResponse) => {
    if (!input.trim()) return;

    showLoading();
    const freshJwtToken = await getFreshToken();
    const newMessages = [...messages, { sender: "user", text: input }];
    setMessages(newMessages);

    const combinedFileContent = tabs.slice(0, 5).map((tab) => ({
      role: "system",
      content: [
        {
          type: "text",
          text: `### File: ${tab.fileName}\n\n${tab.fileContent}`,
        },
      ],
    }));

    const userMessages = [
      ...newMessages.slice(-10).map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: [{ type: "text", text: msg.text }],
      })),
    ];

    const base64Images = await Promise.all(images.map(convertImageToBase64));
    base64Images.forEach((image, index) => {
      userMessages.push({
        role: "user",
        content: [{ type: "image_url", image_url: { url: image } }],
      });
      removeImage(index);
    });

    const requestBody = {
      model,
      messages: [...combinedFileContent, ...userMessages],
    };

    try {
      const response = await fetch(getEndpointUrl() + "/sendPrompt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshJwtToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      processResponse(data.response, newMessages);
    } catch (error) {
      console.error("Error sending prompt:", error);
    } finally {
      hideLoading();
    }
  };

  const handleAppBuilderRequest = async (input, model = "gpt-4o-mini") => {
    console.log("App Builder Mode, selected input & model:", input, model);
    if (!selectedRepoName || selectedRepoName.trim() === "") {
      alert("Empty Selected Repo Name");
      return;
    }
    const newMessages = [...messages, { sender: "user", text: input }];
    setMessages(newMessages);
    showLoading();
    const freshJwtToken = await getFreshToken();

    const base64Images = await Promise.all(images.map(convertImageToBase64));
    const imageMessages = base64Images.map((image, index) => {
      removeImage(index);
      return {
        role: "user",
        content: [{ type: "image_url", image_url: { url: image } }],
      };
    });

    const combinedFileContent = tabs.slice(0, 5).map((tab) => ({
      role: "user",
      content: [
        {
          type: "text",
          text: `### File: ${tab.fileName}\n\n${tab.fileContent}`,
        },
      ],
    }));

    const userMessages = [
      ...newMessages.slice(-10).map((msg) => ({
        role: msg.sender === "user" ? "user" : "assistant",
        content: [{ type: "text", text: msg.text }],
      })),
    ];

    const combinedMessages = [
      ...combinedFileContent,
      ...imageMessages,
      ...userMessages,
    ];
    console.log("Combined Messages:", combinedMessages);

    const requestBody = {
      repoName: selectedRepoName,
      messages: combinedMessages,
      model,
    };

    try {
      const response = await fetch(getEndpointUrl() + "/appBuilderRequest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${freshJwtToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      console.log("App Builder Response:", data);
      // TODO use data.consultedExamples to track already provided examples/docs, create a mechanism to avoid sending same examples/docs
      setMessages([...newMessages, { sender: "bot", text: data.response }]);
      const fileSnippets = extractFileSpecificCode(data.response);
      const repoBasePath = `repos/${selectedRepoName}/`;

      for (const { filePath, codeSnippet } of fileSnippets) {
        const sanitizedFilePath = filePath.replace(/^`|`$/g, "");
        const sanitizedSnippet = codeSnippet.replace(/^`|`$/g, "");

        const finalFilePath = sanitizedFilePath.startsWith(repoBasePath)
          ? sanitizedFilePath
          : `${repoBasePath}${sanitizedFilePath}`;

        const tab = tabs.find((tab) => tab.fileName === finalFilePath);
        const files = [
          {
            filePath: finalFilePath,
            newContent: sanitizedSnippet,
          },
        ];
        if (tab) {
          if (tab.fileContent !== sanitizedSnippet) {
            updateTabContent(finalFilePath, sanitizedSnippet);
            if (quickMode) {
              console.log(
                "[handleAppBuilderRequest] Updating file",
                finalFilePath,
                sanitizedSnippet
              );
              await updateAddFiles(files);
            }
          }
        } else {
          console.log(
            "[handleAppBuilderRequest] Adding tab",
            finalFilePath,
            sanitizedSnippet
          );
          addTab(finalFilePath, sanitizedSnippet, "new");
          if (quickMode) {
            await updateAddFiles(files);
          }
        }
        if (quickMode) {
          console.log("Building project for repo:", selectedRepoName);
          await buildProject(selectedRepoName);
          await new Promise((resolve) => setTimeout(resolve, 5000));
          console.log("Serving project for repo:", selectedRepoName);
          await serveProject(selectedRepoName);
        }
      }
    } catch (error) {
      console.error("Error sending prompt:", error);
    } finally {
      hideLoading();
    }
  };

  const handleModifyFiles = async (input, model = "gpt-4o-mini") => {
    const instruction =
      '. Provide a brief, clear code implementation. Include in your response each updated or new file without abbreviation. Follow the format: "Updated File: path/to/file". Propose a suitable name and path for new files.';

    const newMessages = [...messages, { sender: "user", text: input }];
    setMessages(newMessages);

    if (!selectedRepoName || selectedRepoName.trim() === "") {
      alert("Empty Selected Repo Name");
      return;
    }

    await sendRequestToOpenAI(
      `${input} ${instruction}`,
      model,
      async (response, newMessages) => {
        const fileSnippets = extractFileSpecificCode(response);
        setMessages([...newMessages, { sender: "bot", text: response }]);

        // Dynamically construct the repo base path
        const repoBasePath = `repos/${selectedRepoName}/`;

        for (const { filePath, codeSnippet } of fileSnippets) {
          const sanitizedFilePath = filePath.replace(/^`|`$/g, "");
          const sanitizedSnippet = codeSnippet.replace(/^`|`$/g, "");

          const finalFilePath = sanitizedFilePath.startsWith(repoBasePath)
            ? sanitizedFilePath
            : `${repoBasePath}${sanitizedFilePath}`;

          const tab = tabs.find((tab) => tab.fileName === finalFilePath);
          const files = [
            {
              filePath: finalFilePath,
              newContent: sanitizedSnippet,
            },
          ];
          if (tab) {
            if (tab.fileContent !== sanitizedSnippet) {
              console.log(
                "[sendRequestToOpenAI] Updating file",
                finalFilePath,
                sanitizedSnippet
              );
              updateTabContent(finalFilePath, sanitizedSnippet);
              if (quickMode) {
                await updateAddFiles(files);
              }
            }
          } else {
            console.log(
              "[sendRequestToOpenAI] Adding tab",
              finalFilePath,
              sanitizedSnippet
            );
            addTab(finalFilePath, sanitizedSnippet, "new");
            if (quickMode) {
              await updateAddFiles(files);
            }
          }
        }
        if (quickMode) {
          console.log("Building project for repo:", selectedRepoName);
          await buildProject(selectedRepoName);
          await new Promise((resolve) => setTimeout(resolve, 5000));
          console.log("Serving project for repo:", selectedRepoName);
          await serveProject(selectedRepoName);
        }
      }
    );
  };

  const handleNormalChat = async (input, model = "gpt-4o-mini") => {
    const newMessages = [...messages, { sender: "user", text: input }];
    setMessages(newMessages);
    await sendRequestToOpenAI(input, model, (response, newMessages) => {
      setMessages([...newMessages, { sender: "bot", text: response }]);
    });
  };

  return (
    <OpenAIChatContext.Provider
      value={{
        messages,
        setMessages,
        handleNormalChat,
        handleModifyFiles,
        handleAppBuilderRequest,
        inputText,
        setInputText,
      }}
    >
      {children}
    </OpenAIChatContext.Provider>
  );
};

export const useOpenAIChatContext = () => useContext(OpenAIChatContext);
